import { useEffect, useState } from 'react';

/**
 * Debounces a value by delaying updates until after the specified delay period.
 * Useful for search inputs, form validation, or any scenario where you want to
 * wait for user input to stabilize before taking action.
 *
 * @template T - The type of value being debounced
 * @param value - The value to debounce
 * @param delay - Delay in milliseconds (defaults to 500ms)
 * @returns The debounced value
 *
 * @example
 * const [searchTerm, setSearchTerm] = useState('');
 * const debouncedSearchTerm = useDebounce(searchTerm, 300);
 *
 * useEffect(() => {
 *   // Perform API call with debouncedSearchTerm
 * }, [debouncedSearchTerm]);
 */
function useDebounce<T>(value: T, delay?: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay || 500);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

export default useDebounce;
